/* eslint-disable max-lines */
import { autorun } from 'mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, ContextStore, ExperimentStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';

import { EGMapsBounds, EGMapsCoordinates, UitkEGMapsMarkerType } from 'uitk-react-map';
import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkTertiaryButton } from 'uitk-react-button';

import { FiltersToggle } from 'components/search/sort-filter/filters-toggle';
import { PropertyFields } from 'src/common/__generated__/api';

export interface SearchResultsMapProps extends Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  experiment?: ExperimentStore;
  onDismiss?: () => void;
  searchResults?: SearchResultsStore;
  searchCriteria?: SearchCriteriaModel;
}

export interface SearchResultsMapState {
  center?: any;
  markerSelected?: boolean;
  selectedHotel: PropertyFields.Fragment;
  selectedHotelId?: any;
  selectedMarker: any;
  zoom?: number;
  mapBounds?: any;
  selectedPoiId?: any;
}
export type LocalizedSearchResultsMapProps = LocalizedComponentProps & SearchResultsMapProps;

const EGMaps = React.lazy(() => import('./eg-maps'));
@withRouter
@withLocalization
@inject('analytics', 'context', 'currentHotel', 'experiment', 'searchResults', 'searchCriteria')
@observer
export class SearchResultsMap extends React.Component<LocalizedSearchResultsMapProps, SearchResultsMapState> {
  private zIndex: number;
  private cancelHotelWatch: any;
  private trackMapPan: () => void;
  private trackMapZoomIn: () => void;
  private trackMapZoomOut: () => void;
  private trackMarkerSelect: (clientSideAnalytics) => void;
  private trackClusterSelect: () => void;
  private trackLandmarkSelect: () => void;

  public constructor(props) {
    super(props);
    this.setUpAnalyticsTrackers(props.analytics);
    this.dismissSheet = this.dismissSheet.bind(this);
    this.onMapPan = this.onMapPan.bind(this);
    this.onZoomIn = this.onZoomIn.bind(this);
    this.onZoomOut = this.onZoomOut.bind(this);
    this.onMarkerSelect = this.onMarkerSelect.bind(this);
    this.zIndex = props.searchResults.mapMarkers && props.searchResults.mapMarkers.length ? props.searchResults.properties.length : 0;
    this.state = {
      markerSelected: false,
      selectedHotel: undefined,
      selectedMarker: null,
      center: {},
      selectedPoiId: undefined,
    };
  }

  private setUpAnalyticsTrackers(analytics) {
    const noop = () => null;
    const clickTracker = analytics && analytics.clickTracker ? analytics.clickTracker.bind(analytics) : noop;
    this.trackMapPan = clickTracker('Module.Map.Pan', 'User panning PWA HSR Map', null, noop);
    this.trackMapZoomIn = clickTracker('Module.Map.Zoom.In', 'User zooming in on PWA HSR Map', null, noop);
    this.trackMapZoomOut = clickTracker('Module.Map.Zoom.Out', 'User zooming out on PWA HSR Map', null, noop);
    this.trackMarkerSelect = clientSideAnalytics => {
      const { refererId, linkname } = clientSideAnalytics;
      clickTracker(refererId, linkname, null, null)();
    };
    this.trackClusterSelect = clickTracker('Module.Map.HotelClusterSelect', 'User selecting a cluster on the PWA HSR map', null, noop);
    this.trackLandmarkSelect = clickTracker('Module.Map.LandmarkSelect', 'User selecting a Landmark on the PWA HSR map', null, noop);
  }

  public componentDidMount() {
    const { searchResults, analytics, location } = this.props;
    const locationState = location.state as any;
    this.cancelHotelWatch = autorun(() => {
      if (locationState.selectedHotelId && locationState.selectedHotelId !== this.state.selectedHotel) {
        this.setState({
          selectedHotel: searchResults.findProperty(locationState.selectedHotelId, locationState.selectedHotel?.travelAd),
          selectedMarker: this.findSelectedHotel(locationState.selectedHotelId),
        });
      }
      if (searchResults.hasAtLeastOneProperty) {
        this.setState({
          selectedHotel: undefined,
          selectedMarker: null,
          markerSelected: false,
        });
      }
    });

    const mapFilters =
      searchResults && searchResults.map && searchResults.map.filterDisplayOptions && searchResults.map.filterDisplayOptions.filters;
    if (mapFilters && analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('.MapFilters.Imp', 'Showing map filters', null);
    }
  }

  public componentWillUnmount() {
    if (this.cancelHotelWatch) {
      this.cancelHotelWatch();
      this.cancelHotelWatch = undefined;
    }
  }

  public render() {
    const { context, searchResults } = this.props;
    const mapModel = searchResults && searchResults.map;
    const mapFilters = mapModel && mapModel.filterDisplayOptions && mapModel.filterDisplayOptions.filters;
    const toolBarAttributes = {
      type: ToolbarType.CLOSE,
      header: searchResults.searchedDestination,
      subtext: mapModel && mapModel.subtitle,
      iconLabel: this.props.l10n.formatText('maps.closeButton.label'),
    };
    const toolbarAnalytics = this.props.analytics.setupTracker('.Module.Map.Link', 'Close Map Dialog');
    const dialogProps = {
      dialogShow: true,
      strictFullscreen: true,
    };

    const mapProps = {
      ...mapModel,
      locale: context.locale,
      onZoomIn: this.onZoomIn,
      onZoomOut: this.onZoomOut,
      onMarkerSelect: this.onMarkerSelect,
      onMapPan: this.onMapPan,
      onClusterSelect: this.trackClusterSelect,
      zoom: this.state.zoom || mapModel.zoom,
      mapBounds: this.state.mapBounds,
    };

    const showSheet = this.state.selectedHotel && this.state.selectedMarker && this.state.selectedMarker.sheet;

    const mapSearchProps = {
      l10n: this.props.l10n,
      mapCenter: this.state.center,
      selectedHotel: this.state.selectedHotel,
      selectedMarker: this.state.selectedMarker,
      mapModel,
      showSheet,
      searchResults,
      mapProps,
      mapMarkers: searchResults.mapMarkers,
      propertySearchListings: searchResults?.propertySearchListings,
      selectedPoiId: this.state.selectedPoiId,
      regionId: this.props.searchCriteria.regionId,
    };

    return (
      <UitkFullscreenDialog {...dialogProps}>
        <UitkToolbar
          {...toolBarAttributes}
          action={this.props.onDismiss}
          analytics={toolbarAnalytics}
          actionContent={
            mapFilters && (
              <FiltersToggle
                buttonComponent={UitkTertiaryButton}
                isActive={true}
                isFullWidth={false}
                label="hotels.searchResults.openMapFiltersDialog.label"
                a11yMessage="hotels.searchResults.appliedFiltersCount.a11yLabel"
                rfrr=":MapFilters"
                stid="open-map-filters"
                filters={mapFilters}
                dialogParam="map-filter"
                dialogTitle={this.props.l10n.formatText('hotels.searchResults.openMapFiltersDialog.label')}
              />
            )
          }
        />
        <Experiment name="Top_landmarks_on_SRP">
          <ExperimentControl>
            <React.Suspense fallback={<div />}>
              <EGMaps {...mapSearchProps} />
            </React.Suspense>
          </ExperimentControl>
          <ExperimentVariant bucket={1}>
            <React.Suspense fallback={<div />}>
              <EGMaps {...mapSearchProps} showPois={true} />
            </React.Suspense>
          </ExperimentVariant>
          <ExperimentVariant bucket={2}>
            <React.Suspense fallback={<div />}>
              <EGMaps {...mapSearchProps} showPois={true} enableBoundsComputation={true} />
            </React.Suspense>
          </ExperimentVariant>
        </Experiment>
      </UitkFullscreenDialog>
    );
  }

  public onMarkerSelect = marker => {
    if (marker?.type === UitkEGMapsMarkerType.PROPERTY) {
      this.setState({
        selectedHotelId: marker.id,
      });
      const selectedMarker = this.props.searchResults.mapMarkers.find(x => x?.sheet?.id === marker.id || x?.hotel?.hotelId === marker.id);
      this.trackMarkerSelect(selectedMarker?.clientSideAnalytics);
      this.selectMarker(selectedMarker);
    } else {
      this.setState({ selectedHotelId: undefined, selectedMarker: undefined, markerSelected: false, selectedPoiId: marker.id });
      this.trackLandmarkSelect();
    }
  };

  public onMapPan = (center: EGMapsCoordinates, bounds: EGMapsBounds, zoom: number) => {
    this.trackMapPan();
    this.props.searchResults.displaySearchButton();
    this.setState({
      center: { lat: () => center?.lat, lng: () => center?.lng },
      zoom,
      mapBounds: bounds,
    });
  };

  public onZoomIn(center: EGMapsCoordinates, bounds: EGMapsBounds, zoom: number) {
    this.props.searchResults.displaySearchButton();
    this.setState({
      center: { lat: () => center?.lat, lng: () => center?.lng },
      zoom,
      mapBounds: bounds,
    });
    this.trackMapZoomIn();
  }

  public onZoomOut(center: EGMapsCoordinates, bounds: EGMapsBounds, zoom: number) {
    this.props.searchResults.displaySearchButton();
    this.setState({
      center: { lat: () => center?.lat, lng: () => center?.lng },
      zoom,
      mapBounds: bounds,
    });
    this.trackMapZoomOut();
  }

  private selectMarker(marker) {
    if (this.state.selectedMarker && typeof this.state.selectedMarker.blur === 'function') {
      this.state.selectedMarker.blur();
    }
    this.zIndex += 1;
    if (marker && typeof marker.focus === 'function') {
      marker.focus(this.zIndex);
    }
    this.setState({
      markerSelected: true,
      selectedMarker: marker,
      selectedHotel: this.findSelectedHotelGivenMarker(marker),
    });
  }

  private findSelectedHotelGivenMarker(marker) {
    if (marker && marker.sheet) {
      const foundHotel = marker.sheet.id && this.props.searchResults.findProperty(marker.sheet.id.toString(), marker.sheet.travelAd);

      if (foundHotel) {
        return foundHotel;
      }

      return marker.sheet;
    }

    return marker.hotel;
  }

  private findSelectedHotel(hotelId) {
    if (!hotelId) {
      return undefined;
    }
    const hotels = this.props.searchResults.mapMarkers;
    const hotelContainer = hotels && hotels.find(h => h.id && h.id === hotelId);

    return (hotelContainer && hotelContainer.hotel) || null;
  }

  private dismissSheet() {
    const { selectedMarker } = this.state;
    if (selectedMarker && typeof selectedMarker.blur === 'function') {
      selectedMarker.blur();
    }
    this.setState({
      markerSelected: false,
    });
  }
}
